/**
 * Public chatbot — ``/connect/chatbot/$publicToken`` (anonymous, no auth).
 *
 * An embeddable, white-label customer chatbot backed by an Ando Connect Space.
 * Bootstraps its branding (space name / description) then renders the SAME
 * reusable ``<ConnectChat>`` wired to the anonymous ``askChatbot`` transport —
 * no second chat, DRY.
 *
 * Two presentation modes:
 *   - standalone (default): branded cream canvas, centered card, mobile-first.
 *   - ``?embed=1``: fully chrome-less — transparent background, fills the host
 *     iframe edge-to-edge, no page padding — so a customer can drop it into
 *     their own site without our shell bleeding through.
 *
 * Failure modes resolve to calm cards: not-found / disabled / rate-limited
 * (429). Per-message rate-limit + capped answers are handled inline by
 * ``ConnectChat`` itself (it renders the assistant error bubble / capped note).
 */

import {
  ConnectChat,
  ConnectPublicLoading,
  ConnectPublicNotice,
  ConnectPublicPage,
  ReadOnlyScopeLine,
  SpaceScopeBanner,
} from "@/features/connect";
import {
  type ConnectAskResponse,
  type ConnectChatbotBootstrap,
  ConnectPublicError,
  askChatbot,
  getChatbotBootstrap,
} from "@ando/core";
import { cn } from "@ando/ui";
import { msg } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Bot } from "lucide-react";
import * as React from "react";

interface ChatbotSearch {
  /** ``?embed=1`` → fully chrome-less, iframe-friendly mode. */
  readonly embed: boolean;
}

export const Route = createFileRoute("/connect/chatbot/$publicToken")({
  // Coerce ``?embed=1`` / ``?embed=true`` → boolean; everything else → false.
  validateSearch: (search: Record<string, unknown>): ChatbotSearch => {
    const raw = search.embed;
    const embed = raw === "1" || raw === 1 || raw === true || raw === "true";
    return { embed };
  },
  component: PublicChatbot,
});

function reasonOf(err: unknown): ConnectPublicError | null {
  return err instanceof ConnectPublicError ? err : null;
}

function PublicChatbot(): React.JSX.Element {
  const { i18n } = useLingui();
  const { publicToken } = Route.useParams();
  const { embed } = Route.useSearch();

  const boot = useQuery<ConnectChatbotBootstrap, ConnectPublicError>({
    queryKey: ["connect-chatbot-bootstrap", publicToken],
    queryFn: () => getChatbotBootstrap(publicToken),
    retry: false,
  });

  // Anonymous transport — no credential, public endpoint.
  const ask = React.useCallback(
    async (question: string): Promise<ConnectAskResponse> => {
      return askChatbot(publicToken, { question });
    },
    [publicToken],
  );

  if (boot.isLoading) {
    return <ConnectPublicLoading embed={embed} />;
  }

  const err = reasonOf(boot.error);
  if (boot.isError && err) {
    if (err.reason === "rate_limited") {
      return (
        <ConnectPublicNotice
          embed={embed}
          reason="rate_limited"
          title={<Trans>Troppe richieste</Trans>}
          body={
            <Trans>
              L'assistente è momentaneamente occupato. Attendi qualche istante e riprova.
            </Trans>
          }
        />
      );
    }
    return (
      <ConnectPublicNotice
        embed={embed}
        reason={err.reason === "notfound" || err.reason === "gone" ? "notfound" : "error"}
        title={
          err.reason === "notfound" || err.reason === "gone" ? (
            <Trans>Assistente non disponibile</Trans>
          ) : (
            <Trans>Qualcosa è andato storto</Trans>
          )
        }
        body={
          err.reason === "notfound" || err.reason === "gone" ? (
            <Trans>Questo assistente non è attivo o è stato disabilitato.</Trans>
          ) : (
            <Trans>Non siamo riusciti ad avviare l'assistente. Riprova tra poco.</Trans>
          )
        }
      />
    );
  }

  const data = boot.data;
  const name = data?.name?.trim() || i18n._(msg`Assistente`);
  const description = data?.description?.trim() || null;
  const ownerOrgName = data?.owner_org_name?.trim() || null;
  // Full server-resolved scope chips (same normalized shape as the owner sees).
  const scopeResources = data?.scope?.resources;
  const hasScopeResources = Boolean(scopeResources && scopeResources.length > 0);
  // Sub-line: prefer the assistant's own description, else brand by owning org.
  const subline =
    description ?? (ownerOrgName ? i18n._(msg`di ${ownerOrgName}`) : i18n._(msg`Assistente Ando`));

  // White-label header rendered in the chat's branding slot.
  const header = (
    <div className="flex items-center gap-2.5">
      <span
        aria-hidden
        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-ando-orange-soft text-ando-orange"
      >
        <Bot size={17} strokeWidth={1.85} />
      </span>
      <div className="min-w-0">
        <p className="truncate font-semibold text-[14px] text-ink-1">{name}</p>
        <p className="truncate text-[11.5px] text-ink-2">{subline}</p>
      </div>
    </div>
  );

  const chat = (
    <ConnectChat
      ask={ask}
      title={header}
      className={cn("min-h-0 flex-1", embed && "rounded-none border-0 bg-transparent")}
      scopeBanner={
        // Scope is CONTENT, not chrome: in the chrome-less ``?embed=1`` mode we
        // still declare read-only + what's queryable via the compact
        // ``ReadOnlyScopeLine`` (minimal footprint) so the trust signal never
        // disappears. Standalone shows the fuller chip banner when resolved.
        embed ? (
          <ReadOnlyScopeLine
            heading={<Trans>Risponde solo dalle fonti di questo assistente.</Trans>}
          />
        ) : hasScopeResources ? (
          <SpaceScopeBanner
            readOnly
            heading={<Trans>Risponde solo dalle fonti di questo assistente</Trans>}
            resources={scopeResources}
          />
        ) : (
          <ReadOnlyScopeLine
            heading={<Trans>Risponde solo dalle fonti di questo assistente.</Trans>}
          />
        )
      }
      emptyHint={i18n._(msg`Ciao! Come posso aiutarti? Scrivi qui la tua domanda.`)}
      placeholder={i18n._(msg`Scrivi un messaggio…`)}
    />
  );

  // ── Embed mode: fill the iframe, transparent, zero chrome ────────────
  if (embed) {
    return (
      <ConnectPublicPage embed label={i18n._(msg`Assistente ${name}`)}>
        {chat}
      </ConnectPublicPage>
    );
  }

  // ── Standalone: branded, centered, mobile-first card ─────────────────
  return (
    <ConnectPublicPage label={i18n._(msg`Assistente ${name}`)} className="!py-0">
      <div className="flex min-h-dvh w-full max-w-2xl flex-col py-4 sm:py-6">{chat}</div>
    </ConnectPublicPage>
  );
}
